import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmByTypingDialog } from '@/components/ConfirmByTypingDialog';
import { FramedPane } from '@/components/FramedPane';
import { FramedSearchPane } from '@/components/FramedSearchPane';
import { usePageTitle } from '@/components/layout/page-title';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from '@/components/ui/sonner';
import { ApiError, useMe } from '@/features/auth/api';
import { SessionList } from '@/features/sessions/SessionList';
import { cn } from '@/lib/utils';
import { UserFormDialog } from './UserFormDialog';
import {
  useDeleteUser,
  useForceSyncAllUsers,
  useForceSyncUser,
  useUsers,
  type AdminUser,
} from './api';

export function UsersAdminPage() {
  const { t } = useTranslation();
  usePageTitle(t('admin.users.title'));
  const me = useMe();
  const users = useUsers();
  const deleteUser = useDeleteUser();
  const forceSync = useForceSyncUser();
  const forceSyncAll = useForceSyncAllUsers();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const onSyncAll = () => {
    forceSyncAll.mutate(undefined, {
      onSuccess: () => toast.success(t('admin.users.toasts.sync_all_started')),
      onError: (error) => {
        const message =
          error instanceof ApiError && error.code === 'NO_ACTIVE_CERTIFICATE'
            ? t('admin.users.toasts.sync_all_no_cert')
            : error instanceof ApiError && error.code === 'SYNC_ALREADY_RUNNING'
              ? t('admin.users.toasts.sync_all_already_running')
              : t('admin.users.toasts.sync_all_failed');
        toast.error(message);
      },
    });
  };

  const onSync = (user: AdminUser) => {
    forceSync.mutate(user.id, {
      onSuccess: () => toast.success(t('admin.users.toasts.sync_started')),
      onError: (error) => {
        const message =
          error instanceof ApiError && error.code === 'NO_ACTIVE_CERTIFICATE'
            ? t('admin.users.toasts.sync_no_cert')
            : error instanceof ApiError && error.code === 'SYNC_ALREADY_RUNNING'
              ? t('admin.users.toasts.sync_already_running')
              : t('admin.users.toasts.sync_failed');
        toast.error(message);
      },
    });
  };

  const onDelete = (user: AdminUser) => {
    deleteUser.mutate(user.id, {
      onSuccess: () => {
        toast.success(t('admin.users.toasts.deleted'));
        setSelectedId(null);
      },
      onError: (error) => {
        const message =
          error instanceof ApiError && error.code === 'LAST_ADMIN'
            ? t('admin.users.toasts.delete_last_admin')
            : t('admin.users.toasts.delete_failed');
        toast.error(message);
      },
    });
  };

  const list = users.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return list;
    return list.filter((u) =>
      `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q),
    );
  }, [list, query]);
  const selected = list.find((u) => u.id === selectedId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <PageHeader
        title={t('admin.users.title')}
        description={t('admin.users.description')}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={onSyncAll}
              disabled={forceSyncAll.isPending}
            >
              {forceSyncAll.isPending ? (
                <Loader2 data-icon="inline-start" className="animate-spin" />
              ) : (
                <RefreshCw data-icon="inline-start" />
              )}
              {t('admin.users.sync_all')}
            </Button>
            <UserFormDialog mode={{ kind: 'create' }}>
              <Button type="button">
                <Plus data-icon="inline-start" />
                {t('admin.users.add')}
              </Button>
            </UserFormDialog>
          </>
        }
      />

      <div className="grid min-h-0 flex-1 gap-6 md:grid-cols-[20rem_1fr]">
        <FramedSearchPane
          search={query}
          onSearchChange={setQuery}
          searchPlaceholder={t('admin.users.search_placeholder')}
          listLabel={t('admin.users.title')}
          status={
            users.isPending
              ? 'pending'
              : users.isError
                ? 'error'
                : filtered.length === 0
                  ? 'empty'
                  : 'ready'
          }
          errorMessage={t('admin.users.load_error')}
          emptyMessage={t('admin.users.empty')}
        >
          {filtered.map((user) => (
            <li key={user.id}>
              <UserButton
                user={user}
                selected={user.id === selectedId}
                onSelect={() => setSelectedId(user.id)}
                adminLabel={t('admin.users.role.ADMIN')}
              />
            </li>
          ))}
        </FramedSearchPane>

        {selected ? (
          <FramedPane scroll>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-medium">
                      {selected.firstName} {selected.lastName}
                    </h2>
                    <Badge variant={selected.role === 'ADMIN' ? 'outline' : 'secondary'}>
                      {t(`admin.users.role.${selected.role}`)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{selected.email}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onSync(selected)}
                    disabled={forceSync.isPending}
                  >
                    {forceSync.isPending ? (
                      <Loader2 data-icon="inline-start" className="animate-spin" />
                    ) : (
                      <RefreshCw data-icon="inline-start" />
                    )}
                    {t('admin.users.sync')}
                  </Button>
                  <UserFormDialog mode={{ kind: 'edit', user: selected }}>
                    <Button type="button" variant="outline">
                      <Pencil data-icon="inline-start" />
                      {t('admin.users.edit')}
                    </Button>
                  </UserFormDialog>
                  {selected.id !== me.data?.id ? (
                    <ConfirmByTypingDialog
                      title={t('admin.users.confirm.delete_title')}
                      description={t('admin.users.confirm.delete_description', {
                        name: `${selected.firstName} ${selected.lastName}`,
                        email: selected.email,
                      })}
                      expectedValue={selected.email}
                      inputLabel={t('admin.users.confirm.input_label', { email: selected.email })}
                      placeholder={selected.email}
                      confirmLabel={t('admin.users.delete')}
                      destructive
                      pending={deleteUser.isPending}
                      onConfirm={() => onDelete(selected)}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-destructive"
                      >
                        <Trash2 data-icon="inline-start" />
                        {t('admin.users.delete')}
                      </Button>
                    </ConfirmByTypingDialog>
                  ) : null}
                </div>
              </div>
              <SessionList scope={{ userId: selected.id }} />
            </div>
          </FramedPane>
        ) : (
          <FramedPane>
            <p className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
              {t('admin.users.no_selection')}
            </p>
          </FramedPane>
        )}
      </div>
    </div>
  );
}

function UserButton({
  user,
  selected,
  onSelect,
  adminLabel,
}: {
  user: AdminUser;
  selected: boolean;
  onSelect: () => void;
  adminLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(
        'w-full rounded-md px-3 py-2 text-left transition-colors hover:bg-muted',
        selected && 'bg-muted',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">
          {user.firstName} {user.lastName}
        </span>
        {user.role === 'ADMIN' ? <Badge variant="outline">{adminLabel}</Badge> : null}
      </div>
      <div className="truncate text-xs text-muted-foreground">{user.email}</div>
    </button>
  );
}
